import { useRef, useCallback } from 'react';
import { isMobileDevice, optimizeImageUrl } from '../../utils/imageUtils';
import { debugLog, debugWarn } from '../../utils/debugLog';
import { getCachedImage, isImageCacheAvailable, releaseObjectUrl } from '../../services/imageCacheService';

const MAX_TRACKED_IMAGES = 80;

type NetworkInformationLike = {
  saveData?: boolean;
  effectiveType?: string;
};

const getConnection = (): NetworkInformationLike | null => {
  if (typeof navigator === 'undefined') {
    return null;
  }

  return (
    (navigator as any).connection ||
    (navigator as any).mozConnection ||
    (navigator as any).webkitConnection ||
    null
  );
};

export const useImagePreloader = () => {
  const preloadedImages = useRef(new Set<string>());
  const loadingImages = useRef(new Set<string>());
  const preloadOrder = useRef<string[]>([]);
  const throttledSkips = useRef(0);

  const trimTrackedImages = () => {
    while (preloadOrder.current.length > MAX_TRACKED_IMAGES) {
      const oldest = preloadOrder.current.shift();
      if (oldest) {
        preloadedImages.current.delete(oldest);
      }
    }
  };

  const shouldThrottlePreload = (priority: boolean): boolean => {
    if (priority) {
      return false;
    }

    const connection = getConnection();

    if (connection?.saveData) {
      return true;
    }

    const effectiveType = connection?.effectiveType?.toLowerCase() || '';
    if (effectiveType.includes('2g') || effectiveType === 'slow-2g') {
      return true;
    }

    return false;
  };

  const preloadImage = useCallback((src: string, priority: boolean = false) => {
    const normalizedSrc = optimizeImageUrl(src);

    if (!normalizedSrc || preloadedImages.current.has(normalizedSrc) || loadingImages.current.has(normalizedSrc)) {
      return Promise.resolve();
    }

    if (shouldThrottlePreload(priority)) {
      throttledSkips.current += 1;
      return Promise.resolve();
    }

    loadingImages.current.add(normalizedSrc);

    const clearLoading = () => {
      loadingImages.current.delete(normalizedSrc);
    };

    const markPreloaded = () => {
      preloadedImages.current.add(normalizedSrc);
      preloadOrder.current.push(normalizedSrc);
      trimTrackedImages();
      clearLoading();

      debugLog(`Preloaded ${priority ? '(priority)' : ''}:`, normalizedSrc);
    };

    const fallbackToImageTag = () =>
      new Promise<void>((resolve) => {
        const img = new Image();
        if (typeof window !== 'undefined') {
          try {
            const url = new URL(normalizedSrc, window.location.href);
            if (url.origin === window.location.origin) {
              img.crossOrigin = 'anonymous';
            }
          } catch {
            // Ignore URL parsing errors and skip crossOrigin to avoid CORS failures
          }
        }
        
        img.onload = () => {
          markPreloaded();
          resolve();
        };
        
        img.onerror = () => {
          clearLoading();
          debugWarn('Failed to preload image:', normalizedSrc);
          resolve(); // Resolve anyway to not block other preloads
        };
        
        img.src = normalizedSrc;
      });

    if (isImageCacheAvailable()) {
      return getCachedImage(normalizedSrc)
        .then((result) => {
          if (result.objectUrl) {
            releaseObjectUrl(result.objectUrl);
          }
          markPreloaded();
        })
        .catch((error) => {
          if (process.env.NODE_ENV === 'development') {
            debugWarn('Image cache preload failed, falling back to <img> tag', {
              src: normalizedSrc,
              error
            });
          }
          return fallbackToImageTag();
        });
    }

    return fallbackToImageTag();
  }, []);

  const preloadCommonImages = useCallback((shows: Array<{ images: Record<string, string> }>, priority: boolean = false) => {
    const isMobile = isMobileDevice();
    const maxPreload = isMobile ? 5 : 10; // Preload fewer images on mobile
    
    // Preload the most common presenter images
    shows.slice(0, maxPreload).forEach(show => {
      // Choose optimal size based on device
      const imageSize = isMobile 
        ? (show.images['300x300'] || show.images['150x150'])
        : (show.images['600x600'] || show.images['300x300']);
      
      if (imageSize) {
        preloadImage(imageSize, priority);
      }
    });
  }, [preloadImage]);

  const preloadCriticalImages = useCallback((shows: Array<{ images: Record<string, string> }>) => {
    // Preload first 3 images with high priority (above the fold)
    const criticalShows = shows.slice(0, 3);
    preloadCommonImages(criticalShows, true);
  }, [preloadCommonImages]);

  const clearCache = useCallback(() => {
    preloadedImages.current.clear();
    loadingImages.current.clear();
  }, []);

  const getPreloadStats = useCallback(() => {
    return {
      cacheSize: preloadedImages.current.size,
      isLoading: loadingImages.current.size > 0,
      loadingCount: loadingImages.current.size,
      throttled: throttledSkips.current
    };
  }, []);

  return { 
    preloadImage, 
    preloadCommonImages,
    preloadCriticalImages,
    clearCache,
    getPreloadStats,
    isPreloaded: (src: string) => preloadedImages.current.has(optimizeImageUrl(src))
  };
};

export default useImagePreloader;
